import { useContext, useEffect, useMemo } from "react";
import { useFlexLayout } from "react-table";
import { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
import {
  Box,
  CopyToClipboardButton,
  DashPlaceholder,
  DropDown,
  Flex,
  Icon,
  ModalContext,
  PaginationContext,
  SearchContext,
  SortContext,
  SortHeader,
  Text,
} from "dash-ui";
import { AccountStatusesEnum } from "dash-common";

import DeliveryImage from "assets/images/png/Delivery.png";
import {
  ApiUserForListFragment,
  UserFragment,
  useGetApiUsersQuery,
} from "gql/generated.graphql";

import { StyledTable } from "./ApiCompaniesPageTable.styles";
import { generateApiReferralLink } from "pages/api-management/helpers/helpers";
import { useNavigate } from "react-router-dom";
import { RoutesPath } from "flow-components/routes/routes";

const ApiCompaniesPageTable = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "tables"]);
  const { state, getRefetchProps, setTotal } = useContext(PaginationContext);
  const { state: sortState } = useContext(SortContext);
  const { searchSlug } = useContext(SearchContext);
  const { toggleModal, setPayload } = useContext(ModalContext);
  const pagination = getRefetchProps(state);

  const { data, loading } = useGetApiUsersQuery({
    variables: {
      pagination,
      ...(sortState.fieldName && { sort: [sortState] }),
      filter: {
        ...(searchSlug?.length >= 3 && { searchText: searchSlug }),
      },
    },
  });

  const { users, total } = useMemo(
    () => ({
      users: data?.getApiUsers.collection || [],
      total: data?.getApiUsers.totalCount || 0,
    }),
    [data]
  );

  const navigateToCompanyDetailsPage = (id: string) =>
    navigate(RoutesPath.APIManagementCompanyDetails.replace(":id", id));

  const columns = useMemo(
    () => [
      {
        Header: () => (
          <SortHeader accessor="apiCredentials.companyName">
            {t("forms:labels.apiCompany")}
          </SortHeader>
        ),
        accessor: "apiCredentials.companyName",

        Cell: ({ row }: { row: { original: ApiUserForListFragment } }) => {
          const companyName = row.original.apiCredentials?.companyName ?? "";
          const apiCredentialsId = row.original.apiCredentials?._id ?? "";

          return (
            <Flex
              onClick={() => navigateToCompanyDetailsPage(apiCredentialsId)}
            >
              <Box mr={4}>
                {/* {avatarFile?.publicUrl ? (
                  <Image src={avatarFile.publicUrl} size={32} />
                ) : (
                  <DashPlaceholder size={32} />
                )} */}
                <DashPlaceholder size={32} />
              </Box>
              <Text>{companyName}</Text>
            </Flex>
          );
        },
      },
      {
        Header: () => (
          <SortHeader accessor="profile.firstName" isDefault>
            {t("forms:labels.apiCompanyRepresentative")}
          </SortHeader>
        ),
        accessor: "profile.firstName",
        Cell: ({ row }: { row: { original: ApiUserForListFragment } }) => {
          const { firstName, lastName } = row.original.profile;
          const apiCredentialsId = row.original.apiCredentials?._id ?? "";

          return (
            <Flex
              alignItems="center"
              onClick={() => navigateToCompanyDetailsPage(apiCredentialsId)}
            >
              <Text>
                {firstName} {lastName}
              </Text>
            </Flex>
          );
        },
      },

      {
        Header: () => (
          <SortHeader accessor="account.email">
            {t("forms:labels.email")}
          </SortHeader>
        ),
        accessor: "account.email",

        Cell: ({ value }: { value: string }) => <Text>{value}</Text>,
      },
      {
        Header: () => (
          <SortHeader accessor="clientCount">
            {t("forms:labels.apiClientCount")}
          </SortHeader>
        ),
        accessor: "clientCount",

        Cell: ({ value }: { value: string }) => <Text>{value}</Text>,
      },
      {
        Header: () => (
          <SortHeader accessor="apiCredentials.referralCode">
            {t("forms:labels.apiReferralCode")}
          </SortHeader>
        ),
        accessor: "apiCredentials.referralCode",

        Cell: ({ value }: { value: string }) => {
          const referralLink = generateApiReferralLink(value);
          return (
            <Flex className="cursor-default">
              <Text>{referralLink}</Text>
              {referralLink && (
                <CopyToClipboardButton
                  successMessage="Link copied to clipboard!"
                  textToCopy={referralLink}
                />
              )}
            </Flex>
          );
        },
      },
      {
        Header: () => (
          <SortHeader accessor="monthlyApiAccess">
            {t("forms:labels.apiMonthlyAccess")}
          </SortHeader>
        ),
        accessor: "monthlyApiAccess",

        Cell: ({ value }: { value: string }) => <Text>{value}</Text>,
      },
      {
        Header: () => (
          <Text whiteSpace="nowrap">{t("common:labels.actions")}</Text>
        ),
        accessor: "account._id",
        width: 30,
        Cell: ({ row }: { row: { original: UserFragment } }) => {
          const { _id: userId, status } = row.original.account;
          const isArchived = status === AccountStatusesEnum.ARCHIVED;

          return (
            <Flex alignItems="center" justifyContent="center" h="100%">
              <DropDown
                data-testid="user-actions"
                placementHorizontal="left"
                Anchor={() => <Icon name="dots" />}
                items={[
                  // Archive and delete actions are hidden for now
                  // {
                  //   key: "archive",
                  //   icon: "archive",
                  //   children: isArchived
                  //     ? t("tables:cmsUsers:unarchive")
                  //     : t("tables:cmsUsers:archive"),
                  //   w: 170,
                  //   onClick: () => {
                  //     setPayload({ userId, archive: !isArchived });
                  //     toggleModal(true, "ArchiveUserModal");
                  //   },
                  // },
                  {
                    key: "edit",
                    icon: "edit",
                    children: t("tables:cmsUsers:edit"),
                    w: 170,
                    onClick: () => {
                      setPayload({ user: row.original });
                      toggleModal(true, "AddDashAPIUser");
                    },
                  },
                  // {
                  //   key: "delete",
                  //   icon: "close",
                  //   children: t("tables:cmsUsers:delete"),
                  //   w: 170,
                  //   onClick: () => {
                  //     setPayload({
                  //       accountId: row.original.account._id,
                  //       profileId: row.original.profile._id,
                  //       userName:
                  //         row.original.profile.firstName +
                  //         " " +
                  //         row.original.profile.lastName,
                  //     });
                  //     toggleModal(true, "UserCmsDeleteModal");
                  //   },
                  // },
                ]}
              />
            </Flex>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme, t]
  );

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    setTotal(total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  const labelToShow = !loading
    ? t("pages:managementPage.emptyPlaceholder") || ""
    : "";

  return (
    <StyledTable
      tableProps={{
        data: users,
        columns,
        loading,
        maxHeight: "calc(100vh - 350px)",
        stickyHeader: true,
        layouts: [useFlexLayout],
        placeholderProps: {
          image: loading ? undefined : DeliveryImage,
          label: labelToShow,
        },
      }}
      paginationProps={{
        label: t("tables:organizationUsers.paginationLabel"),
        nextButtonLabel: t("common:labels.next"),
        previousButtonLabel: t("common:labels.previous"),
      }}
    />
  );
};

export default ApiCompaniesPageTable;
